/**
 * Reiter „Daten" der Kundenansicht: alles Hinterlegte in dichten Zeilen, nur
 * zum Ansehen und Antippen (anrufen, mailen, Karte). Geändert wird im Blatt
 * `KundeDatenBlatt` — jede Gruppe hat dafür genau einen hervorgehobenen Knopf
 * „Bearbeiten"; die Aktionen in den Zeilen sind schlichte Zeichen (03.11.2026).
 */
import type { ReactNode } from "react";
import type { Kunde } from "../../lib/types";
import type { KundePflegevertrag } from "../../hooks/usePlanung";
import type { KontaktZeile } from "../../lib/kundeAnlage";
import { MONATE, denormAddr, mapsUrl, type KForm } from "../../lib/kundeFormular";
import { MailKnopf, TelefonLink, kundeMailZiele } from "./KontaktAktionen";
import PflegevertragZeile from "./PflegevertragZeile";
import { KANAL, type DatenBereich } from "./KundeDatenBlatt";
import { ZeichenMail, ZeichenNetz, ZeichenOrt, ZeichenStift, ZeichenTelefon } from "./KundeZeichen";

type Props = {
  kunde: Kunde;
  stand: KForm;
  mitPersonen: boolean;
  vertraege?: KundePflegevertrag[];
  onBearbeiten: (b: DatenBereich) => void;
};

export function Gruppe({ titel, bereich, onBearbeiten, children }: { titel: string; bereich?: DatenBereich; onBearbeiten?: (b: DatenBereich) => void; children: ReactNode }) {
  return (
    <section className="ku-gruppe-karte" aria-label={titel}>
      <div className="ku-gruppe-kopf">
        <h3>{titel}</h3>
        {bereich && onBearbeiten && (
          <button type="button" className="ku-bearbeiten" onClick={() => onBearbeiten(bereich)}><ZeichenStift />Bearbeiten</button>
        )}
      </div>
      {children}
    </section>
  );
}

/** Eine Zeile: Zeichen, Wert, darunter klein die Art — rechts das Zeichen zum Handeln. */
export function Zeile({ zeichen, wert, info, tat }: { zeichen: ReactNode; wert: ReactNode; info?: string; tat?: ReactNode }) {
  return (
    <div className="ku-datenzeile">
      <span className="ku-datenzeile-zeichen" aria-hidden="true">{zeichen}</span>
      <span className="ku-datenzeile-text">
        <span className="ku-datenzeile-wert">{wert}</span>
        {info && <span className="ku-datenzeile-info">{info}</span>}
      </span>
      {tat}
    </div>
  );
}

const ANREDE_WORT: Record<string, string> = { frau: "Frau", herr: "Herr" };

export default function KundeDaten({ kunde, stand, mitPersonen, vertraege, onBearbeiten }: Props) {
  const ziele = kundeMailZiele(kunde);
  const wege = stand.kontakte.filter((k) => k.art === "kontakt");
  const personen = stand.kontakte.filter((k) => k.art === "person");
  const liefer = denormAddr(stand.strasse, stand.plz, stand.ort).replace(/\n/g, ", ");
  const rechnung = denormAddr(stand.rechnung_strasse, stand.rechnung_plz, stand.rechnung_ort).replace(/\n/g, ", ");
  const monate = MONATE.filter(([n]) => Object.prototype.hasOwnProperty.call(stand.pflege_monate, String(n)));

  const anruf = (n: string) => <TelefonLink nummer={n} className="ku-rund"><ZeichenTelefon /></TelefonLink>;
  const mail = (e: string) => <MailKnopf email={e} kundeId={kunde.id} kundeName={kunde.name} ziele={ziele} className="ku-rund"><ZeichenMail /></MailKnopf>;
  const leer = (t: string) => <p className="ku-leer">{t}</p>;

  return (
    <div className="ku-stapel">
      <Gruppe titel="Kontakt" bereich="kontakt" onBearbeiten={onBearbeiten}>
        {!stand.phone && !stand.email && wege.length === 0 && leer("Weder Nummer noch Mailadresse hinterlegt.")}
        {stand.phone && <Zeile zeichen={<ZeichenTelefon />} wert={stand.phone} info={stand.kommunikationskanal === "telefon" ? "Hauptnummer · bevorzugt" : "Hauptnummer"} tat={anruf(stand.phone)} />}
        {stand.email && <Zeile zeichen={<ZeichenMail />} wert={stand.email} info={stand.kommunikationskanal === "mail" ? "Hauptadresse · bevorzugt" : "Hauptadresse"} tat={mail(stand.email)} />}
        {wege.map((w, i) => (w.phone.trim()
          ? <Zeile key={i} zeichen={<ZeichenTelefon />} wert={w.phone.trim()} info={w.role || "Telefon"} tat={anruf(w.phone.trim())} />
          : <Zeile key={i} zeichen={<ZeichenMail />} wert={w.email.trim()} info={w.role || "E-Mail"} tat={mail(w.email.trim())} />))}
        {stand.website && <Zeile zeichen={<ZeichenNetz />} wert={<a href={stand.website} target="_blank" rel="noreferrer">{stand.website}</a>} info="Website" />}
        {stand.kommunikationskanal && !["telefon", "mail"].includes(stand.kommunikationskanal) && (
          <p className="ku-leer">Am liebsten über {KANAL[stand.kommunikationskanal as keyof typeof KANAL]}</p>
        )}
      </Gruppe>

      {mitPersonen && (
        <Gruppe titel="Ansprechpartner" bereich="personen" onBearbeiten={onBearbeiten}>
          {personen.length === 0
            ? leer("Noch niemand hinterlegt — die Firma wird mit „Sehr geehrte Damen und Herren“ angeschrieben.")
            : personen.map((p, i) => <PersonZeile key={i} p={p} anruf={anruf} mail={mail} />)}
        </Gruppe>
      )}

      <Gruppe titel="Anschrift" bereich="anschrift" onBearbeiten={onBearbeiten}>
        {liefer
          ? <Zeile zeichen={<ZeichenOrt />} wert={<a href={mapsUrl(liefer)} target="_blank" rel="noreferrer">{liefer}</a>} info="Lieferung & Baustelle" />
          : leer("Keine Anschrift hinterlegt.")}
        {rechnung
          ? <Zeile zeichen={<ZeichenOrt />} wert={rechnung} info="Rechnung" />
          : liefer && <p className="ku-leer">Rechnung an dieselbe Anschrift</p>}
      </Gruppe>

      <Gruppe titel="Pflege im Jahr" bereich="pflege" onBearbeiten={onBearbeiten}>
        {monate.length === 0 ? leer("Keine regelmäßige Pflege.") : (
          <div className="k-ans-monate" aria-label="Pflegemonate">
            {MONATE.map(([n, lang, kurz]) => {
              const an = Object.prototype.hasOwnProperty.call(stand.pflege_monate, String(n));
              return <span key={n} className={"k-ans-monat" + (an ? " ist-an" : "")} title={an ? `${lang}: ${stand.pflege_monate[String(n)] || "Pflege"}` : lang}>{kurz}</span>;
            })}
          </div>
        )}
        <PflegevertragZeile kundeId={kunde.id} vertraege={Array.isArray(vertraege) ? vertraege : undefined} />
      </Gruppe>

      <Gruppe titel="Interne Notiz" bereich="notiz" onBearbeiten={onBearbeiten}>
        {stand.notes.trim() ? <p className="ku-notiz">{stand.notes}</p> : leer("Keine Notiz.")}
      </Gruppe>
    </div>
  );
}

function PersonZeile({ p, anruf, mail }: { p: KontaktZeile; anruf: (n: string) => ReactNode; mail: (e: string) => ReactNode }) {
  const name = [ANREDE_WORT[p.anrede] || "", p.vorname, p.nachname].filter(Boolean).join(" ") || p.name || p.email || p.phone;
  return (
    <div className="ku-datenzeile">
      <span className="ku-datenzeile-text">
        <span className="ku-datenzeile-wert">{name}</span>
        <span className="ku-datenzeile-info">{[p.role, p.email, p.phone].filter(Boolean).join(" · ")}</span>
      </span>
      {p.phone && anruf(p.phone)}
      {p.email && mail(p.email)}
    </div>
  );
}
